import {useModel} from "hooks"
import styles from "./MediaLibrary.module.css"
import {ReactComponent as IconUpload} from "./icons/upload-to-cloud.svg"
import cx from "classnames"
import {BaseButton, BaseInput, Icon} from "components"
import {getPreviewUnavailableImage, onErrorImageLoad} from "helpers"
import ReactPlayer from "react-player/lazy"
import {PDFViewer} from "components/FileViewer"
import {FormLabel} from "components/Form"
import {MediaLibraryItemSubType, MediaLibraryItemType} from "types/media-library"
import {useDropzone} from "react-dropzone"
import {useMemo} from "react"

const FILE_TYPE = {
    image: {
        validations: "image/*",
        types: ["JPEG", "PNG"]
    },
    video: {
        validations: "video/*",
        types: ["MP4"]
    },
    document: {
        validations: "application/pdf",
        types: ["PDF"]
    },
    link: {
        validations: "",
        types: []
    }
}

const UPLOAD_MEDIA_HEADING_LABEL = {
    [MediaLibraryItemType.Image]: "Upload image",
    [MediaLibraryItemType.Video]: "Upload preview",
    [MediaLibraryItemType.Document]: "Upload document",
    [MediaLibraryItemType.Link]: "Upload media"
}

const LINK_MEDIA_HEADING_LABEL = {
    [MediaLibraryItemType.Image]: "Image preview",
    [MediaLibraryItemType.Video]: "Upload Video",
    [MediaLibraryItemType.Document]: "Document preview",
    [MediaLibraryItemType.Link]: "Preview"
}

const ALTERNATE_PREVIEW_HEADING_LABEL = {
    [MediaLibraryItemType.Image]: "Alternate image preview",
    [MediaLibraryItemType.Video]: "Alternative preview image for the video (Optional)",
    [MediaLibraryItemType.Document]: "Document image preview",
    [MediaLibraryItemType.Link]: "Link preview"
}

const PREVIEW_IMAGE_HEADING = {
    [MediaLibraryItemType.Image]: "Alternative Preview Image",
    [MediaLibraryItemType.Video]: "Alternative Preview Image",
    [MediaLibraryItemType.Document]: "Alternative Preview Image",
    [MediaLibraryItemType.Link]: "Alternative Preview Image"
}

type Props = {
    mediaLibraryData: any
    itemType: MediaLibraryItemType
    itemSubType: MediaLibraryItemSubType
    link?: string
    showRemoveThumbnailIcon?: boolean
    thumbnailImage?: string
    imageSource?: string
    mediaFile?: File
    errors?: any
    onChangeLink?: (value: string) => void
    onRemoveFile: () => void
    onRemoveImageThumbnail: () => void
    onDropFiles: (acceptedFiles: any) => void
    onDropThumbnails: (acceptedFiles: any) => void
    maxFileSizeInMb?: number
}

export function FileCardHandler(props: Props) {
    const {
        mediaLibraryData,
        itemType,
        itemSubType,
        link,
        showRemoveThumbnailIcon,
        thumbnailImage,
        imageSource,
        mediaFile,
        errors,
        onChangeLink,
        onRemoveFile,
        onRemoveImageThumbnail,
        onDropFiles,
        onDropThumbnails,
        maxFileSizeInMb
    } = props
    const model = useModel()

    const {getRootProps, getInputProps} = useDropzone({
        multiple: false,
        onDrop: onDropFiles,
        accept: itemType ? FILE_TYPE[itemType]?.validations : ""
    })

    const {getRootProps: getRootPropsForImageThumbnail, getInputProps: getInputPropsForImageThumbnail} = useDropzone({
        multiple: false,
        onDrop: onDropThumbnails,
        accept: "image/*"
    })

    const mediaFileUrl = useMemo(() => {
        return mediaFile ? URL.createObjectURL(mediaFile) : undefined
    }, [mediaFile])

    const renderUploadFile = () => {
        if (itemSubType === MediaLibraryItemSubType.Link) {
            if (itemType === MediaLibraryItemType.Link) return null

            let isMediaAvailable: boolean = true
            let imgSrc = link ?? imageSource
            if (!imgSrc) {
                isMediaAvailable = false
                imgSrc = getPreviewUnavailableImage(model.clientSetting.slug)
            }

            return (
                <div className={styles.previewImageContainer} key="5">
                    <p className={styles.uploadCardHeadingLabel}>{LINK_MEDIA_HEADING_LABEL[itemType]}</p>
                    <div
                        className={cx(styles.mediaPreviewContainer, {
                            [styles.listingMediaPreviewContainerDetailPage]:
                                mediaLibraryData.type === MediaLibraryItemType.Document
                        })}>
                        {itemType === MediaLibraryItemType.Image || !isMediaAvailable ? (
                            <img
                                className={styles.mediaImage}
                                src={imgSrc}
                                alt=""
                                onError={(currentTarget) => onErrorImageLoad(model.clientSetting.slug, currentTarget)}
                            />
                        ) : itemType === MediaLibraryItemType.Video ? (
                            <ReactPlayer url={imgSrc} controls />
                        ) : null}
                    </div>
                </div>
            )
        } else {
            if (imageSource) {
                return (
                    <div className={styles.previewImageContainer}>
                        {itemType !== MediaLibraryItemType.Link && (
                            <p className={styles.uploadCardHeadingLabel}>{LINK_MEDIA_HEADING_LABEL[itemType]}</p>
                        )}
                        <div
                            className={cx(styles.mediaPreviewContainer, {
                                [styles.listingMediaPreviewContainerDetailPage]:
                                    mediaLibraryData.type === MediaLibraryItemType.Document
                            })}>
                            <span onClick={onRemoveFile}>
                                <Icon
                                    icon="CROSS_CIRCLE_BORDER"
                                    className={cx(styles.icon, styles.mediaPreviewClose)}
                                />
                            </span>
                            {itemType === MediaLibraryItemType.Image ? (
                                <img
                                    className={styles.mediaImage}
                                    src={imageSource ?? getPreviewUnavailableImage(model.clientSetting.slug)}
                                    alt=""
                                    onError={(currentTarget) =>
                                        onErrorImageLoad(model.clientSetting.slug, currentTarget)
                                    }
                                />
                            ) : itemType === MediaLibraryItemType.Video ? (
                                <ReactPlayer url={imageSource} controls />
                            ) : (
                                itemType === MediaLibraryItemType.Document && (
                                    <PDFViewer
                                        fileSrc={imageSource ?? mediaFileUrl}
                                        options={{toolbar: false, zoom: "fitToWidth", view: "fitToWidth"}}
                                    />
                                )
                            )}
                        </div>
                    </div>
                )
            }
            return (
                <div className={styles.previewImageContainer} key="3">
                    <p className={styles.uploadCardHeadingLabel}>{UPLOAD_MEDIA_HEADING_LABEL[itemType]}</p>
                    <div {...getRootProps()} className={cx(styles.dropzoneItem, styles.bgColorGray)}>
                        <input {...getInputProps()} />
                        <div className={styles.iconUploadWrapper}>
                            <IconUpload width="32" height="32" className={styles.iconUpload} />
                        </div>
                        <p className={styles.dropzone__heading}>
                            UPLOAD {itemType?.toUpperCase()} {maxFileSizeInMb ? `(${maxFileSizeInMb}MB MAX)` : ""}
                        </p>
                        <p className={styles.dropzone__text}>
                            Drag &amp; drop a {itemType} {itemType ? FILE_TYPE[itemType].types.join(" or ") : ""} format
                            into this box or
                        </p>
                        <BaseButton title="Browse to upload" className={styles.dropzone__btn} />
                    </div>
                </div>
            )
        }
    }

    const renderUploadPreviewThumbnail = () => {
        return (
            <div className={styles.previewImageContainer}>
                <p className={styles.uploadCardHeadingLabel}>{ALTERNATE_PREVIEW_HEADING_LABEL[itemType]}</p>
                <div className={styles.uploadCardWrapper}>
                    <div className={styles.previewImageWrapper} key="8">
                        <div {...getRootPropsForImageThumbnail()} className={styles.dropzoneItem}>
                            <input {...getInputPropsForImageThumbnail()} />
                            <div className={styles.iconUploadWrapper}>
                                <IconUpload width="32" height="32" className={styles.iconUpload} />
                            </div>
                            <p className={styles.dropzone__heading}>{PREVIEW_IMAGE_HEADING[itemType].toUpperCase()}</p>
                            <p className={styles.dropzone__text}>
                                Drag &amp; drop an image JPEG or PNG file into this box or
                            </p>
                            <BaseButton title="Browse to upload" className={styles.dropzone__btn} variant="secondary" />
                        </div>
                    </div>

                    <div className={styles.previewThumbnailWrapper} key="7">
                        <div className={styles.mediaPreviewWrapper}>
                            <div className={styles.mediaPreviewContainer}>
                                {showRemoveThumbnailIcon && thumbnailImage && (
                                    <span onClick={onRemoveImageThumbnail}>
                                        <Icon
                                            icon="CROSS_CIRCLE_BORDER"
                                            className={cx(styles.icon, styles.mediaPreviewClose)}
                                        />
                                    </span>
                                )}
                                {thumbnailImage && (
                                    <img
                                        className={styles.mediaImage}
                                        src={thumbnailImage}
                                        alt=""
                                        onError={(currentTarget) =>
                                            onErrorImageLoad(model.clientSetting.slug, currentTarget)
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderContent = () => {
        if (!itemType || !itemSubType) return null
        return (
            <>
                {renderUploadFile()}
                {renderUploadPreviewThumbnail()}
            </>
        )
    }

    return (
        <>
            {itemSubType === MediaLibraryItemSubType.Link && (
                <div className={styles.formGroup} key="5">
                    <FormLabel label="Link" isRequired />
                    <BaseInput
                        value={link}
                        onChange={onChangeLink}
                        placeholder="Link"
                        error={!!errors[MediaLibraryItemSubType.Link]}
                    />
                </div>
            )}
            <div className={styles.fileCardHandler}>{renderContent()}</div>
        </>
    )
}
